import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import PlayCircleOutlineOutlined from "@mui/icons-material/PlayCircleOutlineOutlined";
import { buildPreviewModel } from "../videoSourceUtils";

export default function ClippingVideoPreview({ sourceType, url, startSec, lang }) {
  const theme = useTheme();
  const [fileError, setFileError] = React.useState(false);
  const preview = React.useMemo(
    () => buildPreviewModel(sourceType, url, startSec),
    [sourceType, url, startSec],
  );

  React.useEffect(() => {
    setFileError(false);
  }, [url, sourceType]);

  if (preview.kind === "empty") return null;

  if (preview.kind === "invalid") {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {preview.message}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.75}>
        {lang?.clippingPreview || "Preview"}
      </Typography>
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.common.black, 0.06),
          border: "1px solid",
          borderColor: "divider",
          aspectRatio: "16 / 9",
          maxHeight: 360,
        }}
      >
        {preview.kind === "youtube" && (
          <Box
            component="iframe"
            key={`${preview.videoId}-${preview.startSec}`}
            src={preview.embedUrl}
            title={lang?.clippingYoutubePreview || "YouTube preview"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        )}

        {preview.kind === "file" && !fileError && (
          <Box
            component="video"
            src={preview.fileUrl}
            controls
            playsInline
            preload="metadata"
            onError={() => setFileError(true)}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              bgcolor: "#000",
            }}
          />
        )}

        {preview.kind === "file" && fileError && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              textAlign: "center",
            }}
          >
            <PlayCircleOutlineOutlined sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary">
              {lang?.clippingPreviewUnavailable ||
                "Preview unavailable — the URL may block browser playback. You can still submit the job."}
            </Typography>
          </Box>
        )}
      </Box>
      {preview.kind === "youtube" && preview.startSec > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
          {(lang?.clippingPreviewStart || "Playback starts at {sec}s").replace(
            "{sec}",
            String(Math.floor(preview.startSec)),
          )}
        </Typography>
      )}
    </Box>
  );
}
